import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { useLazyQuery } from "@apollo/react-hooks";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import emailIcon from "@iconify/icons-mdi/email";
import keyIcon from "@iconify/icons-mdi/key";
import { InlineIcon } from "@iconify/react";
import Validator from "fastest-validator";
import { MY_HR_OW_ADMIN_LOGIN, MY_HR_REQUEST_PERMIT } from "my-hr/graphql";
import { useOcPermit } from "oc/hooks";
import useOcProperty from "oc/hooks/use-property";

interface MyHROwadminLoginPageProps {
  onLogin: (loginResult: any) => void;
}

export const MyHROwadminLoginPage: React.FC<MyHROwadminLoginPageProps> = ({
  onLogin,
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const { data: permit, error: permitError } = useOcPermit();

  /** Email states */
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailErrorText, setEmailErrorText] = useState<string>("");

  /** Password states */
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorText, setPasswordErrorText] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);

  /** Button state */
  const [submitButtonState, setSubmitButtonState] = useState(false);

  /** Validator stuff */
  const validator = new Validator();
  const schema = {
    email: {
      type: "email",
      messages: {
        email: t("MY_HR_ERROR_MESSAGE_WRONG_EMAIL_FORMAT"),
        emailEmpty: t("OC_ERROR_MESSAGE_EMAIL_IS_REQUIRED"),
      },
    },
    password: {
      type: "string",
      min: 1,
      messages: {
        string: "Nem megfelelő jelszó!",
        stringMin: t("MY_HR_ERROR_MESSAGE_PASSWORD_IS_REQUIRED"),
      },
    },
  };
  const check = validator.compile(schema);

  const [requestPermit, { data: requestedPermit }] = useMutation(
    MY_HR_REQUEST_PERMIT,
    {
      onCompleted: () => {
        setEmailErrorText(t("MY_HR_PERMIT_GRANTED"));
      },
      onError: (error) => {
        console.log({ error });
      },
    },
  );

  const [login, { data: loginData, error: loginError }] = useLazyQuery(MY_HR_OW_ADMIN_LOGIN, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (loginData?.owLogin) {
      onLogin(loginData.owLogin);
      let { from } = (location.state as { from?: { pathname: string } }) || {
        from: { pathname: "/" },
      };
      setSubmitButtonState(false);
      history.replace(from || { pathname: "/" });
    }
  }, [loginData, onLogin, location.state, history]);

  useEffect(() => {
    if (loginError?.graphQLErrors) {
      loginError.graphQLErrors.forEach((item) => {
        if (item.message === "No user found!") {
          setEmailErrorText(t("MY_HR_ERROR_MESSAGE_NO_USER_FOUND"));
          setEmailError(true);
        }
        if (item.message === "Wrong password!") {
          setPasswordErrorText(t("MY_HR_ERROR_MESSAGE_WRONG_PASSWORD"));
          setPasswordError(true);
        }
        if (item.message === "User account has expired!") {
          setEmailErrorText(t("MY_HR_ERROR_MESSAGE_ACCOUNT_HAS_EXPIRED"));
          setEmailError(true);
        }
      });
      setSubmitButtonState(false);
    }
  }, [loginError, t]);

  const { data: passwordVisibilityData } = useOcProperty({
    resource: "system",
    key: "passwordVisibility",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let isValidated = await check({ email, password });
    if (isValidated !== true) {
      isValidated.forEach((item: any) => {
        if (item.field === "email") {
          setEmailErrorText(item.message);
          setEmailError(true);
        }
        if (item.field === "password") {
          setPasswordErrorText(item.message);
          setPasswordError(true);
        }
      });
      return;
    }
    setSubmitButtonState(true);
    login({ variables: { email, password } });
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleRequestPermit = () => {
    requestPermit({ variables: { locale: i18n.language } });
  };

  return (
    <Box
      width="100%"
      height="100%"
      style={{ backgroundColor: "#cc0033" }}
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <Box
        width="400px"
        height="100%"
        style={{ backgroundColor: "white" }}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Box id="orgware-text-logo">
          <img
            alt="orgware-icon"
            src="/img/owLogoColored.png"
            style={{ width: 300 }}
          />
        </Box>
        <Typography
          variant="body1"
          style={{ color: "#cc0033", marginTop: "32px" }}
        >
          Orgware admin login
        </Typography>
        {permit?.permittedAt && !permitError && !permit?.cancelledAt ? (
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Box width="304px" paddingTop="38px">
              <TextField
                id="email"
                defaultValue={email}
                onChange={(e) => setEmail(e.target.value)}
                label={t("MY_HR_EMAIL")}
                variant="outlined"
                fullWidth
                error={emailError}
                onFocus={() => {
                  setEmailErrorText("");
                  setEmailError(false);
                }}
                helperText={emailError && emailErrorText}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <InlineIcon height="24px" icon={emailIcon} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                id="password"
                defaultValue={password}
                onChange={(e) => setPassword(e.target.value)}
                label={t("MY_HR_PASSWORD")}
                variant="outlined"
                type={showPassword ? "text" : "password"}
                fullWidth
                helperText={passwordErrorText}
                error={passwordError}
                onFocus={() => {
                  setPasswordErrorText("");
                  setPasswordError(false);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <InlineIcon height="24px" icon={keyIcon} />
                      <Box width="8px" />
                      {passwordVisibilityData === "true" && (
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleShowPassword}
                          style={{
                            backgroundColor: "#F1F3F4",
                            height: "48px",
                            width: "48px",
                          }}
                        >
                          {showPassword ? (
                            <VisibilityIcon
                              style={{ height: "24px", width: "24px" }}
                            />
                          ) : (
                            <VisibilityOffIcon
                              style={{ height: "24px", width: "24px" }}
                            />
                          )}
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
                style={{ marginTop: "36px" }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disableElevation
                disabled={submitButtonState}
                style={{
                  marginTop: "36px",
                }}
              >
                {t("MY_HR_LOGIN")}
              </Button>
            </Box>
          </form>
        ) : (
          <Box width="304px" paddingTop="36px">
            {requestedPermit && !permit?.cancelledAt && !permitError && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                paddingBottom="24px"
              >
                <Typography>{t("MY_HR_PERMIT_REQUESTED")}</Typography>
              </Box>
            )}
            <Button
              variant="contained"
              color="primary"
              disableElevation
              fullWidth
              onClick={handleRequestPermit}
            >
              {t("MY_HR_REQUEST_PERMIT")}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MyHROwadminLoginPage;
